import { useQuery } from "@tanstack/react-query";
import { useProperty } from "../../../../components/property-provider";
import { useCategoryLimitsApi } from "../../infrastructure/api/categoryLimits.api";
import { useExtrasCategoriesFlag } from "./useExtrasCategoriesFlag";

export const categoryLimitsQueryKeys = {
  all: ["accommodationCategoryLimits"] as const,
  detail: (propertyTicker: string, accommodationId: string | undefined, locale: string) =>
    [...categoryLimitsQueryKeys.all, propertyTicker, accommodationId, locale] as const,
};

/**
 * `GetAccommodationCategoryLimits` for the open accommodation form.
 *
 * `accommodationId` is `""` on create and `undefined` when no form is open, which disables
 * the query, as does the flag being off. The list keeps its own key, so it never holds a form's
 * result. The result is a snapshot of the open form: it is never refetched in the background
 * (a new instance would reload the form and discard the user's edits) and is not kept once the
 * form is left (`gcTime: 0`), so reopening reloads it. A failure is not retried and leaves the
 * form usable without the section.
 */
export function useAccommodationCategoryLimitsQuery(
  accommodationId: string | undefined,
  locale: string
) {
  const flagActive = useExtrasCategoriesFlag();
  const { ticker } = useProperty();
  const { getAccommodationCategoryLimits } = useCategoryLimitsApi();

  return useQuery({
    queryKey: categoryLimitsQueryKeys.detail(ticker, accommodationId, locale),
    queryFn: () => getAccommodationCategoryLimits(accommodationId ?? "", locale),
    enabled: flagActive && accommodationId !== undefined,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: 0,
  });
}
